import React from 'react';
import { Security as Shield, Gavel, CheckCircle } from '@mui/icons-material';

const TermsOfServicePage = () => {
    const lastUpdated = "December 1, 2024";

    const sections = [
        {
            title: "1. Acceptance of Terms",
            content: [
                "By accessing and using the RiskAS platform, you accept and agree to be bound by the terms and provision of this agreement.",
                "If you do not agree to abide by the above, please do not use this service.",
                "These Terms of Service may be updated from time to time. Your continued use of RiskAS constitutes acceptance of any changes."
            ]
        },
        {
            title: "2. Use License",
            content: [
                "Permission is granted to temporarily download one copy of RiskAS materials for personal, non-commercial transitory viewing only.",
                "This is the grant of a license, not a transfer of title, and under this license you may not:",
                "• modify or copy the materials",
                "• use the materials for any commercial purpose or for any public display (commercial or non-commercial)",
                "• attempt to decompile or reverse engineer any software contained on RiskAS's platform",
                "• remove any copyright or other proprietary notations from the materials"
            ]
        },
        {
            title: "3. Account Terms",
            content: [
                "You must be 18 years or older to use this Service.",
                "You must be human. Accounts registered by automated methods are not permitted.",
                "You are responsible for maintaining the security of your account and password.",
                "You are responsible for all activities that occur under your account.",
                "You may not use RiskAS for any illegal or unauthorized purpose.",
                "You must not, in the use of the Service, violate any laws in your jurisdiction."
            ]
        },
        {
            title: "4. Service Availability",
            content: [
                "RiskAS strives to maintain 99.9% uptime, but cannot guarantee uninterrupted service.",
                "We reserve the right to temporarily disable your account if we believe you have violated these terms.",
                "We may modify or discontinue the Service at any time with reasonable notice.",
                "Scheduled maintenance will be announced in advance when possible."
            ]
        },
        {
            title: "5. Data and Privacy",
            content: [
                "Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information.",
                "You retain ownership of any data you submit to RiskAS.",
                "We will not sell, rent, or share your personal information with third parties without your consent.",
                "We implement industry-standard security measures to protect your data.",
                "You may export your data at any time through our platform."
            ]
        },
        {
            title: "6. Payment Terms",
            content: [
                "Some services require payment. You'll be clearly informed of any costs before you incur them.",
                "All fees are exclusive of applicable taxes unless stated otherwise.",
                "Subscription fees are charged in advance on a recurring basis.",
                "We offer a full refund within 14 days of your initial subscription.",
                "Downgrading your service may result in loss of features, content, or capacity."
            ]
        },
        {
            title: "7. Prohibited Uses",
            content: [
                "You may not use RiskAS to:",
                "• Upload or transmit viruses or malicious code",
                "• Spam, phish, or send unsolicited communications",
                "• Violate any applicable laws or regulations",
                "• Infringe on intellectual property rights",
                "• Harass, abuse, or harm others",
                "• Interfere with or disrupt the Service"
            ]
        },
        {
            title: "8. Intellectual Property",
            content: [
                "The Service and its original content, features, and functionality are owned by RiskAS and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.",
                "You may not copy, modify, distribute, sell, or lease any part of our Services.",
                "Our trademarks and trade dress may not be used without our prior written consent."
            ]
        },
        {
            title: "9. Limitation of Liability",
            content: [
                "RiskAS shall not be liable for any indirect, incidental, special, consequential, or punitive damages.",
                "Our total liability to you for any claim shall not exceed the amount paid by you for the Service in the 12 months preceding the claim.",
                "Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability for incidental damages, so some limitations may not apply to you."
            ]
        },
        {
            title: "10. Termination",
            content: [
                "Either party may terminate this agreement at any time with written notice.",
                "Upon termination, your right to use the Service will cease immediately.",
                "We may suspend or terminate your account for violation of these terms.",
                "You may cancel your subscription at any time through your account settings."
            ]
        },
        {
            title: "11. Governing Law",
            content: [
                "These terms shall be governed and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.",
                "Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights."
            ]
        },
        {
            title: "12. Contact Information",
            content: [
                "If you have any questions about these Terms of Service, please contact us at:",
                "Email: legal@riskas.com",
                "Address: 123 Innovation Drive, San Francisco, CA 94105",
                "Phone: +1 (555) 123-4567"
            ]
        }
    ];

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            {/* Header Spacer */}
            <div style={{ height: '64px' }}></div>

            {/* Hero Section */}
            <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <Gavel style={{ fontSize: 48, color: '#2563eb' }} />
                    </div>
                    <h1 style={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        marginBottom: '24px',
                        color: '#111827'
                    }}>
                        Terms of Service
                    </h1>
                    <p style={{
                        fontSize: '20px',
                        color: '#6b7280',
                        maxWidth: '800px',
                        margin: '0 auto',
                        lineHeight: '1.6'
                    }}>
                        Please read these terms carefully before using our services.
                        These terms govern your use of RiskAS and constitute a legal agreement between you and us.
                    </p>
                    <div style={{
                        marginTop: '32px',
                        padding: '16px 24px',
                        background: 'rgba(37, 99, 235, 0.1)',
                        borderRadius: '8px',
                        display: 'inline-block'
                    }}>
                        <span style={{ color: '#2563eb', fontWeight: '600' }}>
                            Last updated: {lastUpdated}
                        </span>
                    </div>
                </div>
            </section>

            {/* Key Points Section */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '32px', textAlign: 'center' }}>
                        Key Points Summary
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <CheckCircle style={{ fontSize: 32, color: '#16a34a', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                                Fair Usage
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                Use our service responsibly and in accordance with applicable laws and regulations.
                            </p>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <Shield style={{ fontSize: 32, color: '#2563eb', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                                Data Protection
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                Your data is protected with enterprise-grade security and remains under your control.
                            </p>
                        </div>
                        <div style={{
                            background: '#f9fafb',
                            padding: '24px',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            textAlign: 'center'
                        }}>
                            <Gavel style={{ fontSize: 32, color: '#7c3aed', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                                Clear Terms
                            </h3>
                            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                                Transparent terms with no hidden clauses. Easy cancellation and fair refund policy.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Terms Content */}
            <section style={{ padding: '80px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e5e7eb'
                    }}>
                        {sections.map((section, index) => (
                            <div key={index} style={{
                                padding: '32px',
                                borderBottom: index < sections.length - 1 ? '1px solid #f3f4f6' : 'none'
                            }}>
                                <h2 style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#111827',
                                    marginBottom: '16px'
                                }}>
                                    {section.title}
                                </h2>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {section.content.map((paragraph, pIndex) => (
                                        <p key={pIndex} style={{
                                            color: '#374151',
                                            lineHeight: '1.6',
                                            fontSize: '16px',
                                            margin: 0
                                        }}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section style={{ padding: '60px 0', background: 'white' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                        Questions About Our Terms?
                    </h2>
                    <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
                        If you have any questions about these Terms of Service, please don't hesitate to contact our legal team.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button
                            onClick={() => window.location.href = '/contact'}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            Contact Legal Team
                        </button>
                        <button
                            onClick={() => window.location.href = '/privacy-policy'}
                            style={{
                                background: 'white',
                                color: '#2563eb',
                                border: '2px solid #2563eb',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            View Privacy Policy
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TermsOfServicePage;